import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MyOrder, Order, Segment } from 'ts/shared/interfaces/MOResponse';
import { getStatus } from 'constants/mo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/ui/Button/Button';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import { Select } from 'ts/interfaces/Generic';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import {
  faArrowDown,
  faArrowsUpDown,
  faArrowsUpToLine,
  faArrowsDownToLine,
  faArrowUp,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import useModalClass from 'hooks/useModalClass';
import { createPortal } from 'react-dom';
import { changeSegmentEditor, getCommision, getMyOrdersCurrencies } from 'services/myOrders';
import { showError, showSuccess } from 'utils/utils';
import withErrorBoundary from '../../withErrorBoundary';
import ScreenshotSupport from '../../ScreenshotSupport';
import MaximizeModal from '../../MaximizeModal';
import { dateFormat } from 'utils/shared/utils';
import useDraggableModal from 'hooks/useDraggableModal';

interface MoChangeGrossModalProps {
  myOrder: MyOrder;
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
  onSubmit: (myOrder: MyOrder) => void;
}


const MoChangeGrossModal: FC<MoChangeGrossModalProps> = ({
  myOrder,
  openModal,
  setOpenModal,
  ...props
}) => {
  const [t] = useTranslation();
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const classList = useModalClass(openModal).replace('modal-sm', 'modal-st');
  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(true);
  const [orderModel, setOrderModel] = useState<Order>(myOrder.order);
  const segment: any = orderModel.segments.find(s => s.orderSegId === myOrder.fullSegment.orderSegId);
  const [hightlightedInputs, setHightlightedInputs] = useState<string[]>([]);
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currencies, setCurrencies] = useState<Select[]>([]);
  const [commision, setCommision] = useState<any>(null);

  useEffect(() => {
    if (openModal) {
      (async () => {
        const currencies = await getMyOrdersCurrencies();
        setCurrencies(currencies);

        const comission = await getCommision({ supplierCode: myOrder.fullSegment.sysSuppCode, productType: myOrder.fullSegment.productType });
        setCommision({ ...comission });

      })();

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModal]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        window.clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  const onChangeSegment = (segment: Segment) => {
    setOrderModel({ ...orderModel, segments: orderModel.segments.map(s => s.orderSegId === segment.orderSegId ? segment : s) });
  };

  const level1Markup = Number(commision?.markUpPresentLevel_1 || 0);
  const level2Markup = Number(commision?.markUpPresentLevel_2 || 0);
  const roundTo2 = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;
  const applyMarkup = (value: number, markup: number) => roundTo2(value * (1 + markup));
  const removeMarkup = (value: number, markup: number) => {
    const divider = 1 + markup;
    if (divider === 0) return 0;
    return roundTo2(value / divider);
  };

  const onClickDownNett = () => {
    if (!segment) return;
    const nettValue = Number(segment.sysTotalNett || 0);
    const grossLevel1 = applyMarkup(nettValue, level1Markup);
    onChangeSegment({ ...segment, sysGross: grossLevel1, sysTotalGross: roundTo2(grossLevel1 - (segment?.discountAmount || 0)) });
    highlightInputs(['sysGross', 'sysTotalGross']);
  };

  const onClickDownNettAll = () => {
    if (!segment) return;
    const nettValue = Number(segment.sysTotalNett || 0);
    const grossLevel1 = applyMarkup(nettValue, level1Markup);
    const grossLevel2 = applyMarkup(nettValue, level2Markup);
    onChangeSegment({
      ...segment,
      sysGross: grossLevel1,
      sysTotalGross: roundTo2(grossLevel1 - (segment?.discountAmount || 0)),
      sysGross2: grossLevel2,
      sysTotalGross2: roundTo2(grossLevel2 - (segment?.discountAmount || 0) - (segment?.discountCouponAmount || 0)),
    });
    highlightInputs(['sysGross', 'sysTotalGross', 'sysGross2', 'sysTotalGross2']);
  }

  const onClickDownGross = () => {
    if (!segment) return;
    const grossValue = Number(segment.sysGross || 0);
    const grossLevel2 = applyMarkup(grossValue, level2Markup);
    onChangeSegment({ ...segment, sysGross2: grossLevel2, sysTotalGross2: roundTo2(grossLevel2 - (segment?.discountAmount || 0) - (segment?.discountCouponAmount || 0)) });
    highlightInputs(['sysGross2', 'sysTotalGross2']);
  }

  const onClickUpGross = () => {
    if (!segment) return;
    const grossValue = Number(segment.sysGross || 0);
    const nettValue = removeMarkup(grossValue, level1Markup);
    onChangeSegment({ ...segment, sysTotalNett: nettValue });
    highlightInputs(['sysTotalNett']);
  }

  const onClickDownGrossAll = () => {
    if (!segment) return;
    const grossValue = Number(segment.sysGross || 0);
    const nettValue = removeMarkup(grossValue, level1Markup);
    const grossLevel2 = applyMarkup(grossValue, level2Markup);
    onChangeSegment({ ...segment, sysTotalNett: nettValue, sysGross2: grossLevel2, sysTotalGross2: roundTo2(grossLevel2 - (segment?.discountAmount || 0) - (segment?.discountCouponAmount || 0)) });
    highlightInputs(['sysTotalNett', 'sysGross2', 'sysTotalGross2']);
  }

  const onClickUpGross2 = () => {
    if (!segment) return;
    const grossValue = Number(segment.sysGross2 || 0);
    const nettValue = removeMarkup(grossValue, level2Markup);
    const grossLevel1 = applyMarkup(nettValue, level1Markup);
    onChangeSegment({ ...segment, sysTotalGross: roundTo2(grossLevel1 - (segment?.discountAmount || 0)), sysGross: grossLevel1 });
    highlightInputs(['sysTotalGross', 'sysGross']);
  }

  const onClickUpGross2All = () => {
    if (!segment) return;
    const grossValue = Number(segment.sysGross2 || 0);
    const nettValue = removeMarkup(grossValue, level2Markup);
    const grossLevel1 = applyMarkup(nettValue, level1Markup);
    onChangeSegment({ ...segment, sysTotalNett: nettValue, sysGross: grossLevel1, sysTotalGross: roundTo2(grossLevel1 - (segment?.discountAmount || 0)) });
    highlightInputs(['sysTotalNett', 'sysGross', 'sysTotalGross']);
  }

  const onChangeDisctountAmount = (amount: string) => {
    const amountValue = Number(amount);
    onChangeSegment({
      ...segment!,
      discountAmount: amountValue,
      sysTotalGross: roundTo2(segment?.sysGross - amountValue),
      sysTotalGross2: roundTo2(segment?.sysGross2 - amountValue - (segment?.discountCouponAmount || 0)),
    });
    highlightInputs(['sysTotalGross', 'sysTotalGross2']);
  }

  const onChangeDisctountCouponAmount = (amount: string) => {
    const amountValue = Number(amount);
    onChangeSegment({ ...segment!, discountCouponAmount: amountValue, sysTotalGross2: roundTo2(segment?.sysGross2 - amountValue - segment?.discountAmount || 0) });
    highlightInputs(['sysTotalGross', 'sysTotalGross2']);
  }

  const highlightInputs = (input: string[]) => {
    setHightlightedInputs(input);
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    highlightTimeoutRef.current = setTimeout(() => {
      setHightlightedInputs([]);
    }, 2000);
  };

  const onClickSave = async () => {
    if (!segment) return;

    setIsLoading(true);
    const segmentResult = {
      ...segment,
      sysGross: roundTo2(Number(segment.sysGross)),
      sysTotalGross: roundTo2(Number(segment.sysTotalGross)),
      sysGross2: roundTo2(Number(segment.sysGross2)),
      sysTotalGross2: roundTo2(Number(segment.sysTotalGross2)),
      sysTotalNett: roundTo2(Number(segment.sysTotalNett)),
      discountAmount: roundTo2(Number(segment.discountAmount)),
      discountCouponAmount: roundTo2(Number(segment.discountCouponAmount)),
      sysCurrencyCode: segment.sysCurrencyCode,
      vat_rate: roundTo2(Number(segment.vat_rate)),
    };
    const newSegments = myOrder.order.segments.map(s => s.orderSegId === segment.orderSegId ? segmentResult : s);

    const result = await changeSegmentEditor({
      order: { ...myOrder.order, segments: newSegments },
      changePrice: true,
    });

    if (result) {
      showSuccess(t('mo.segment_gross_edited'));
      props.onSubmit({ ...result, orderRow: { ...result.orderRow, status: myOrder.orderRow.status } });
      setOpenModal(false);
    } else {
      showError(t('something_went_wrong'));
    }

    setIsLoading(false);
  }

  return createPortal(
    <div
      className="modal-overlay modal-overlay-top mo-message-modal-overlay modal-maximizable"
      style={{ direction: 'ltr' }}
      ref={modalOverlayRef}
    >
      <div ref={modalRef} className={classList + ' modal'} style={modalStyle}>
        <div
          className={'modal-title draggable-modal-handle' + (isDragging ? ' dragging' : '')}
          {...dragHandleProps}
        >
          <h5 className="title">
            Segment Gross Editror{' '}
            {myOrder?.fullSegment && (
              <Fragment>
                {' '}-{' '}Order: {myOrder.orderRow.orderId}{' '}
                Segment:
                {myOrder.fullSegment.orderSegId}{' '}
                <span className={`mo-status ${getStatus(myOrder.orderRow.status)}`}>
                  {myOrder.orderRow.status}
                </span>
              </Fragment>
            )}
          </h5>
          <div data-no-drag="true">
            <ScreenshotSupport />
            <MaximizeModal modalOverlayRef={modalOverlayRef} />
            <FontAwesomeIcon icon={faTimes} onClick={() => setOpenModal(false)} />
          </div>
        </div>

        <div className="modal-body modal-body-overflow">
          <div className="mt-10"></div>
          {myOrder.fullSegment.sysSuppCode === 'btop' ? (
            <div className="block-wr block-danger" style={{ marginTop: 0 }}>
              <span className="text-bold">No Commision Found</span>
              <span className="ml-5">
                for product [{myOrder.fullSegment.suppItemDesc}] in supplier [
                <span className="text-bold ml-5">
                  {myOrder.fullSegment.sysSuppDesc} ({myOrder.fullSegment.sysSuppCode})
                </span>
                ]
              </span>
            </div>
          ) : (
            <div className="mo-block">
              <div className="mo-block-title">
                <span>Info</span>
              </div>
              <div className="mo-block-content">
                <div className="row">
                  <FormGroup
                    className="col-md-2 col-6 mb-15"
                    onChange={() => { }}
                    value={myOrder.fullSegment.productType || ''}
                    label={'Product'}
                    disabled={true}
                    movablePlaceholder={'Product'}
                    movablePlaceholderSm={true}
                  />
                  <FormGroup
                    className="col-md-3 col-6 mb-15"
                    onChange={() => { }}
                    value={`${myOrder.fullSegment.sysSuppDesc} (${myOrder.fullSegment.sysSuppCode})`}
                    label={'Supplier'}
                    disabled={true}
                    movablePlaceholder={'Supplier'}
                    movablePlaceholderSm={true}
                  />
                  <FormGroup
                    className="col-md-2 col-6 mb-15"
                    onChange={() => { }}
                    value={(Number((commision?.markUpPresentLevel_1 || 0) * 100)).toFixed(2) + '%'}
                    label={'Level 1'}
                    disabled={true}
                    movablePlaceholder={'Level 1'}
                    movablePlaceholderSm={true}
                  />
                  <FormGroup
                    className="col-md-2 col-6 mb-15"
                    onChange={() => { }}
                    value={(Number((commision?.markUpPresentLevel_2 || 0) * 100)).toFixed(2) + '%'}
                    label={'Level 2'}
                    disabled={true}
                    movablePlaceholder={'Level 2'}
                    movablePlaceholderSm={true}
                  />
                  <FormGroup
                    className="col-md-3 col-6 mb-15"
                    onChange={() => { }}
                    value={dateFormat(myOrder.fullSegment.dateU, 'dd/MM/yy HH:mm')}
                    label={'Last Update'}
                    disabled={true}
                    movablePlaceholder={'Last Update'}
                    movablePlaceholderSm={true}
                  />
                </div>
              </div>
            </div>
          )}
          <div className="mt-20"></div>
          <div className="mo-block">
            <div className="mo-block-title">
              <span>Gross</span>
            </div>
            <div className="mo-block-content">
              <div className="row">
                <div className={`col-6 mb-15 ${hightlightedInputs.includes('sysTotalNett') ? 'mo-highlight-green' : ''}`}>
                  <FormGroup
                    onChange={(val) => {
                      onChangeSegment({ ...segment!, sysTotalNett: val });
                    }}
                    value={segment?.sysTotalNett.toString() || ''}
                    label={'Nett'}
                    movablePlaceholder={'Nett'}
                    movablePlaceholderSm={true}
                    allowOnlyNumbers={true}
                  />
                </div>
                <div className="col-3">
                  <Button title={'Down'} icon={faArrowDown} className="btn btn-default w100p" onClick={onClickDownNett} />
                </div>
                <div className="col-3">
                  <Button
                    title={'Down All'}
                    icon={faArrowsDownToLine}
                    className="btn btn-default w100p"
                    onClick={onClickDownNettAll}
                  />
                </div>
                <div className="col-12 mb-20 mt-5">
                  <div className="divider"></div>
                </div>
                <div
                  className={`col-3 mb-15 ${hightlightedInputs.includes('sysGross') ? 'mo-highlight-green' : ''}`}
                >
                  <FormGroup
                    onChange={(val) => {
                      onChangeSegment({ ...segment!, sysGross: val, sysTotalGross: val });
                      highlightInputs(['sysTotalGross']);
                    }}
                    value={segment?.sysGross.toString() || ''}
                    label={'Gross Level 1'}
                    movablePlaceholder={'Gross Level 1'}
                    movablePlaceholderSm={true}
                    allowOnlyNumbers={true}
                  />
                </div>
                <div className="col-3">
                  <Button title={'Down'} icon={faArrowDown} className="btn btn-default w100p" onClick={onClickDownGross} />
                </div>
                <div className="col-3">
                  <Button title={'Up'} icon={faArrowUp} className="btn btn-default w100p" onClick={onClickUpGross} />
                </div>

                <div className="col-3">
                  <Button
                    title={'Down Up'}
                    icon={faArrowsUpDown}
                    className="btn btn-default w100p"
                    onClick={onClickDownGrossAll}
                  />
                </div>
                <div className={`col-12 mb-15 ${hightlightedInputs.includes('discountAmount1') ? 'mo-highlight-green' : ''}`}>
                  <FormGroup
                    onChange={(val) => {
                      onChangeDisctountAmount(val);
                    }}
                    value={segment?.discountAmount?.toString() || ''}
                    label={'Discount Amount'}
                    movablePlaceholder={'Discount Amount'}
                    movablePlaceholderSm={true}
                    allowOnlyNumbers={true}
                  />
                </div>
                <div
                  className={`col-12 mb-15 form-group-black ${hightlightedInputs.includes('sysTotalGross') ? 'mo-highlight-green' : ''}`}
                >
                  <FormGroup
                    onChange={() => { }}
                    value={segment?.sysTotalGross.toString() || ''}
                    label={'Total Gross 1'}
                    movablePlaceholder={'Total Gross 1'}
                    movablePlaceholderSm={true}
                    allowOnlyNumbers={true}
                    disabled={true}
                  />
                </div>
                <div className="col-12 mb-20 mt-5">
                  <div className="divider"></div>
                </div>

                <div className={`col-6 mb-15 ${hightlightedInputs.includes('sysGross2') ? 'mo-highlight-green' : ''}`}>
                  <FormGroup
                    onChange={(val) => {
                      onChangeSegment({ ...segment!, sysGross2: val });
                    }}
                    value={segment?.sysGross2.toString() || ''}
                    label={'Gross Level 2'}
                    movablePlaceholder={'Gross Level 2'}
                    movablePlaceholderSm={true}
                    allowOnlyNumbers={true}
                  />
                </div>
                <div className="col-3">
                  <Button title={'Up'} icon={faArrowUp} className="btn btn-default w100p" onClick={onClickUpGross2} />
                </div>
                <div className="col-3">
                  <Button
                    title={'Up All'}
                    icon={faArrowsUpToLine}
                    className="btn btn-default w100p"
                    onClick={onClickUpGross2All}
                  />
                </div>
                <div className={`col-6 mb-15 ${hightlightedInputs.includes('discountAmount2') ? 'mo-highlight-green' : ''}`}>
                  <FormGroup
                    onChange={(val) => {
                      onChangeSegment({ ...segment!, discountAmount: val });
                    }}
                    value={segment?.discountAmount?.toString() || ''}
                    label={'Discount Amount'}
                    movablePlaceholder={'Discount Amount'}
                    movablePlaceholderSm={true}
                    disabled={true}
                  />
                </div>
                <div className={`col-6 mb-15 ${hightlightedInputs.includes('discountCouponAmount') ? 'mo-highlight-green' : ''}`}>
                  <FormGroup
                    onChange={(val) => {
                      onChangeDisctountCouponAmount(val);
                    }}
                    value={segment?.discountCouponAmount?.toString() || ''}
                    label={'Discount Coupon Amount'}
                    movablePlaceholder={'Discount Coupon Amount'}
                    movablePlaceholderSm={true}
                    allowOnlyNumbers={true}
                  />
                </div>

                <div className={`col-12 mb-15 form-group-black ${hightlightedInputs.includes('sysTotalGross2') ? 'mo-highlight-green' : ''}`}>
                  <FormGroup
                    onChange={(val) => {
                      onChangeSegment({ ...segment!, sysTotalGross2: val });
                    }}
                    value={segment?.sysTotalGross2?.toString() || ''}
                    label={'Total Gross 2'}
                    movablePlaceholder={'Total Gross 2'}
                    movablePlaceholderSm={true}
                    disabled={true}

                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-20"></div>

          <div className="mo-block">
            <div className="mo-block-title">
              <span>Other</span>
            </div>
            <div className="mo-block-content">
              <div className="row">
                <div className="col-6 mb-15">
                  <SelectCustom
                    onChange={(val) => onChangeSegment({ ...segment!, sysCurrencyCode: val })}
                    value={segment?.sysCurrencyCode || ''}
                    options={currencies}
                    placeholder={'Currency'}
                    movablePlaceholder={'Currency'}
                    appendToBody={true}
                    menuPlacement='top'
                  />
                </div>
                <div className="col-6 mb-15">
                  <FormGroup
                    onChange={(val) => onChangeSegment({ ...segment!, vat_rate: val })}
                    value={segment?.vat_rate?.toString() || ''}
                    label={'VAT Rate'}
                    movablePlaceholder={'VAT Rate'}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <Button disabled={isLoading} title={t('save')} className="btn btn-primary ml-0 mr-a" onClick={onClickSave} />
          <Button
            title={t('cancel')}
            className="btn btn-default"
            onClick={() => setOpenModal(false)}
          />
        </div>
      </div>
    </div>
    , document.body);
};

export default withErrorBoundary(MoChangeGrossModal, 'MoChangeGrossModal');
